"use client";

import { Button } from "../../../../shadcn/ui/button";
import { Input } from "../../../../shadcn/ui/input";
import { Label } from "../../../../shadcn/ui/label";
import { Textarea } from "../../../../shadcn/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../shadcn/ui/select";
import { RadioGroup, RadioGroupItem } from "../../../../shadcn/ui/radio-group";
import { Switch } from "../../../../shadcn/ui/switch";
import { ArrowLeft, Upload, Users, Coins, Plus, X, Trash2, Calendar, Settings, HelpCircle, Clock } from "lucide-react";
import type { ListingData } from "./index";
import { useState, useRef } from "react";
import { useCurrencies } from "../../../../hooks/useCurrencies";
import { useUserCurrency } from "../../../../hooks/useUserCurrency";
import TimeInput from "./TimeInput";
import { useEffect } from "react";
import ValidationMessage from "./ValidationMessage";
import SaveStatusIndicator from "./SaveStatusIndicator";
import { RotateCcw } from "lucide-react";
import logger from "../../../../utils/logger";


interface StepTwoProps {
  data: ListingData;
  updateData: (data: Partial<ListingData>) => void;
  onNext: () => void;
  onPrev: () => void;
  lastSaved: Date | null;
  onClearForm: () => void;
  isEditMode?: boolean;
}

export default function StepTwo({
  data,
  updateData,
  onNext,
  onPrev,
  lastSaved,
  onClearForm,
  isEditMode,
}: StepTwoProps) {
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { currencies, currenciesLoading } = useCurrencies();
  const userCurrencyCode = useUserCurrency();
  const userCurrency = currencies.find((c: any) => c.code === userCurrencyCode);

  // Update form data with user currency when it's available
  useEffect(() => {
    if (userCurrencyCode && data.currency !== userCurrencyCode) {
      updateData({ currency: userCurrencyCode });
    }
  }, [userCurrencyCode, data.currency]);

  const addTicketType = () => {
    const newTicket = {
      id: Date.now().toString(),
      name: "",
      price: "",
      limit: "",
      ticketType: "OFFLINE",
    };
    updateData({ ticketTypes: [...data.ticketTypes, newTicket] });
  };

  const removeTicketType = (id: string) => {
    updateData({ ticketTypes: data.ticketTypes.filter(t => t.id !== id) });
  };

  const updateTicketType = (id: string, field: string, value: string) => {
    updateData({
      ticketTypes: data.ticketTypes.map(t => (t.id === id ? { ...t, [field]: value } : t)),
    });
  };

  // Image upload functions
  const handleFiles = (files: FileList) => {
    logger.log("📸 [DEBUG] handleFiles called with:", files.length, "files");

    const validImageTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"];
    const maxFileSize = 5 * 1024 * 1024; // 5MB limit
    const maxImages = 8;

    const currentImages = (data.uploadedImages || []) as Array<{
      id: string | number;
      file: File;
      url: string;
      name: string;
      size: number;
    }>;
    logger.log("📸 [DEBUG] Current images:", currentImages.length);

    const remainingSlots = maxImages - currentImages.length;
    logger.log("📸 [DEBUG] Remaining slots:", remainingSlots);

    if (remainingSlots <= 0) {
      logger.log("📸 [DEBUG] Maximum images reached");
      return;
    }

    let oversizedFiles: string[] = [];
    let invalidTypeFiles: string[] = [];

    const validFiles = Array.from(files)
      .slice(0, remainingSlots)
      .filter(file => {
        if (!validImageTypes.includes(file.type)) {
          invalidTypeFiles.push(file.name);
          return false;
        }
        if (file.size > maxFileSize) {
          oversizedFiles.push(file.name);
          return false;
        }
        return true;
      });

    if (oversizedFiles.length > 0) {
      logger.log("📸 [DEBUG] Oversized files:", oversizedFiles);
    }

    if (invalidTypeFiles.length > 0) {
      logger.log("📸 [DEBUG] Invalid type files:", invalidTypeFiles);
    }

    if (validFiles.length > 0) {
      logger.log("📸 [DEBUG] Processing valid files:", validFiles.length);

      const newImages = validFiles.map((file, index) => {
        const imageObj = {
          id: Date.now() + Math.random() + index,
          file: file,
          url: URL.createObjectURL(file),
          name: file.name,
          size: file.size,
        };
        logger.log("📸 [DEBUG] Created image object:", { id: imageObj.id, name: imageObj.name, size: imageObj.size });
        return imageObj;
      });

      const updatedImages = [...currentImages, ...newImages];
      logger.log("📸 [DEBUG] Setting images to:", updatedImages.length, "total images");

      updateData({ uploadedImages: updatedImages });
    } else {
      logger.log("📸 [DEBUG] No valid files to process");
    }
  };

  const removeImage = (imageId: string | number) => {
    logger.log("📸 [DEBUG] removeImage called for ID:", imageId);

    const currentImages = (data.uploadedImages || []) as Array<{
      id: string | number;
      file: File;
      url: string;
      name: string;
      size: number;
    }>;
    logger.log("📸 [DEBUG] Current images before removal:", currentImages.length);

    const imageToRemove = currentImages.find(img => img.id === imageId);
    logger.log("📸 [DEBUG] Image to remove found:", !!imageToRemove);

    if (imageToRemove && imageToRemove.url && imageToRemove.url.startsWith("blob:")) {
      logger.log("📸 [DEBUG] Revoking blob URL");
      URL.revokeObjectURL(imageToRemove.url);
    }

    const updatedImages = currentImages.filter(img => img.id !== imageId);
    logger.log("📸 [DEBUG] Updated images after removal:", updatedImages.length);

    updateData({ uploadedImages: updatedImages });
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    logger.log("📸 [DEBUG] handleFileSelect called", { files: e.target.files });

    if (e.target.files && e.target.files.length > 0) {
      logger.log(
        "📸 [DEBUG] Processing files:",
        Array.from(e.target.files).map(f => ({ name: f.name, size: f.size, type: f.type }))
      );
      handleFiles(e.target.files);
    } else {
      logger.log("📸 [DEBUG] No files selected");
    }
  };

  // URL Regex pattern
  const urlPattern = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/i;

  const isValidUrl = (url: string | undefined | null) => {
    if (!url) return false;
    return urlPattern.test(url.trim());
  };

  // Validation for Step 2
  const isValid =
    data.description?.trim() &&
    data.capacity &&
    data.servicePrice &&
    data.currency &&
    data.duration &&
    data.size?.trim() &&
    (data.uploadedImages?.length || 0) > 0 && // At least one image required
    (data.availabilitySlots?.length || 0) > 0 && // At least one availability slot
    (data.ticketTypes?.length || 0) > 0 && // At least one ticket type required
    data.ticketTypes.every(t => t.ticketType === "DEFAULT" || isValidUrl(t.url)); // Valid URL required for OFFLINE

  const getValidationMessage = () => {
    if (!data.description?.trim()) return "Please enter an experience description";
    if (!data.capacity) return "Please enter the maximum capacity";
    if (!data.servicePrice) return "Please enter the service price";
    if (!data.currency) return "Please select a currency";
    if (!data.duration) return "Please select a duration type";
    if (!data.size?.trim()) return "Please enter the space/event size";
    if (!data.uploadedImages || data.uploadedImages.length === 0) return "Please upload at least one image";
    if (!data.availabilitySlots || data.availabilitySlots.length === 0)
      return "Please set at least one availability slot";
    if (!data.ticketTypes || data.ticketTypes.length === 0) return "Please add at least one ticket type";
    if (!data.ticketTypes.every(t => t.ticketType === "DEFAULT" || t.url?.trim()))
      return "Please provide a URL for all OFFLINE ticket types";
    if (!data.ticketTypes.every(t => t.ticketType === "DEFAULT" || isValidUrl(t.url)))
      return "Please provide a valid website URL (e.g. google.com)";
    return null;
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="border-b bg-background">
        <div className="mx-auto max-w-4xl px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={onPrev}
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Basics
            </button>
            <div className="flex items-center gap-4">
              <span className="text-sm text-muted-foreground">Step 2 of 4</span>
              <SaveStatusIndicator lastSaved={lastSaved} />
              {!isEditMode && (
                <Button variant="ghost" size="sm" onClick={onClearForm} className="gap-2" {...({} as any)}>
                  <RotateCcw className="h-4 w-4" />
                  Start Over
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="border-b bg-background">
        <div className="mx-auto max-w-4xl px-6 py-6">
          <h1 className="mb-2 text-2xl font-semibold">
            {isEditMode ? `Edit: ${data.title || "Play Service"}` : "Create New Play Service"}
          </h1>
          <div className="mb-2 h-2 w-full overflow-hidden rounded-full bg-muted">
            <div className="h-full w-1/2 bg-foreground transition-all" />
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Details & Media</span>
            <span className="text-muted-foreground">50% Complete</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="mx-auto max-w-2xl px-6 py-12">
        <div className="mb-12 text-center">
          <h2 className="mb-2 text-3xl font-semibold">
            {isEditMode ? "Update details & media" : "Show off your experience"}
          </h2>
          <p className="text-muted-foreground">Add rich content and set your pricing to attract participants</p>
        </div>

        <div className="space-y-8">
          {/* Experience Description */}
          <div className="rounded-lg border bg-card p-6">
            <h3 className="mb-4 font-semibold">Experience Description</h3>
            <div className="space-y-2">
              <Label htmlFor="description" {...({} as any)}>
                Tell participants what to expect *
              </Label>
              <Textarea
                id="description"
                placeholder="Lorem Ipsum"
                value={data.description}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  updateData({ description: e.target.value.slice(0, 250) })
                }
                rows={6}
                className="resize-none"
                maxLength={250}
                {...({} as any)}
              />
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>{data.description.length}/250 characters</span>
                <span>Be detailed and engaging</span>
              </div>
            </div>
          </div>

          {/* Photos & Videos */}
          <div className="rounded-lg border bg-card p-6">
            <div className="mb-4 flex items-center gap-2">
              <Upload className="h-5 w-5" />
              <h3 className="font-semibold">Photos & Videos *</h3>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-xs text-muted-foreground">
                  {(data.uploadedImages || []).length}/8 images (max 5MB each)
                </span>
              </div>

              {/* Drag and Drop Area */}
              <div
                className={`border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors cursor-pointer ${
                  dragActive ? "border-blue-500 bg-blue-50" : ""
                } ${(data.uploadedImages || []).length >= 8 ? "opacity-50 pointer-events-none" : ""}`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                  disabled={(data.uploadedImages || []).length >= 8}
                />
                <Upload className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                <p className="text-gray-500 text-sm mb-2">
                  {(data.uploadedImages || []).length >= 8 ? "Maximum images reached" : "Drag & drop your media here"}
                </p>
                {(data.uploadedImages || []).length < 8 && (
                  <>
                    <p className="text-gray-500 text-sm mb-2">or</p>
                    <span className="text-blue-600 hover:text-blue-700 text-sm font-medium">Browse Files</span>
                  </>
                )}
                <p className="text-xs text-gray-400 mt-1">PNG, JPG, GIF, WEBP up to 5MB</p>
              </div>

              {/* Uploaded Images Grid */}
              {(data.uploadedImages || []).length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {(data.uploadedImages || []).map((image: any) => (
                    <div key={image.id} className="relative group">
                      <img src={image.url} alt={image.name} className="w-full h-20 object-cover rounded-lg border" />
                      <button
                        type="button"
                        onClick={() => removeImage(image.id)}
                        className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-75 group-hover:opacity-100 hover:bg-red-600 transition-all duration-200 shadow-sm"
                        title={`Remove ${image.name}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                      <div className="absolute bottom-1 left-1 bg-black bg-opacity-50 text-white text-xs px-1 py-0.5 rounded">
                        {(image.size / 1024).toFixed(0)}KB
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Show message when no images uploaded */}
              {(data.uploadedImages || []).length === 0 && (
                <div className="text-center py-3">
                  <p className="text-gray-500 text-sm">Please upload at least one image to continue</p>
                </div>
              )}
            </div>
          </div>

          {/* Capacity */}
          <div className="rounded-lg border bg-card p-6">
            <div className="mb-4 flex items-center gap-2">
              <Users className="h-5 w-5" />
              <h3 className="font-semibold">Capacity</h3>
            </div>
            <div className="space-y-2">
              <Label htmlFor="capacity" {...({} as any)}>
                Maximum number of participants *
              </Label>
              <Input
                id="capacity"
                type="number"
                placeholder="20"
                value={data.capacity}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateData({ capacity: e.target.value })}
                {...({} as any)}
              />
              <p className="text-xs text-muted-foreground">
                Consider your space limitations and desired experience quality
              </p>
            </div>
          </div>

          {/* Size */}
          <div className="rounded-lg border bg-card p-6">
            <div className="mb-4 flex items-center gap-2">
              <Users className="h-5 w-5" />
              <h3 className="font-semibold">Size</h3>
            </div>
            <div className="space-y-2">
              <Label htmlFor="size" {...({} as any)}>
                Space/Event Size
              </Label>
              <Input
                id="size"
                placeholder="e.g., 40 sqm or 400 sq ft"
                value={data.size || ""}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateData({ size: e.target.value })}
                {...({} as any)}
              />
              <p className="text-xs text-muted-foreground">Enter space size in sqm or sq ft</p>
            </div>
          </div>

          {/* Service Price */}
          <div className="rounded-lg border bg-card p-6">
            <div className="mb-4 flex items-center gap-2">
              <Coins className="h-5 w-5" />
              <h3 className="font-semibold">Service Price</h3>
            </div>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="servicePrice" {...({} as any)}>
                  Price per participant *
                </Label>
                <Input
                  id="servicePrice"
                  type="number"
                  placeholder="50"
                  value={data.servicePrice || ""}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateData({ servicePrice: e.target.value })}
                  {...({} as any)}
                />
                <p className="text-xs text-muted-foreground">The base price for your play service experience</p>
              </div>

              {/* Currency Display (Read-only) */}
              <div className="space-y-2">
                <Label {...({} as any)}>Currency *</Label>
                <div className="px-3 py-2 text-sm bg-gray-50 border border-gray-300 rounded-md text-gray-700">
                  {currenciesLoading
                    ? "Loading..."
                    : userCurrency
                      ? `${userCurrency.code} - ${userCurrency.name}`
                      : userCurrencyCode || "KES"}
                </div>
                <p className="text-xs text-muted-foreground">Currency is based on your country setting</p>
              </div>
            </div>
          </div>

          {/* Duration */}
          <div className="rounded-lg border bg-card p-6">
            <div className="mb-4 flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              <h3 className="font-semibold">Duration Type</h3>
            </div>
            <div className="space-y-2">
              <Label {...({} as any)}>Booking Duration *</Label>
              <RadioGroup
                value={data.duration ? data.duration.toLowerCase() : undefined}
                onValueChange={(value: string) => updateData({ duration: value.toUpperCase() })}
                {...({} as any)}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="hourly" id="hourly" {...({} as any)} />
                  <Label htmlFor="hourly" className="cursor-pointer" {...({} as any)}>
                    per hour
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="daily" id="daily" {...({} as any)} />
                  <Label htmlFor="daily" className="cursor-pointer" {...({} as any)}>
                    per day
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="weekly" id="weekly" {...({} as any)} />
                  <Label htmlFor="weekly" className="cursor-pointer" {...({} as any)}>
                    per week
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="monthly" id="monthly" {...({} as any)} />
                  <Label htmlFor="monthly" className="cursor-pointer" {...({} as any)}>
                    per month
                  </Label>
                </div>
              </RadioGroup>
              <p className="text-xs text-muted-foreground">Select the duration unit for booking this play service</p>
            </div>
          </div>

          {/* Ticket Types & Pricing */}
          <div className="rounded-lg border bg-card p-6">
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Coins className="h-5 w-5" />
                <h3 className="font-semibold">Ticket Types & Pricing</h3>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={addTicketType}
                className="gap-2 bg-transparent"
                {...({} as any)}
              >
                <Plus className="h-4 w-4" />
                Add Ticket Type
              </Button>
            </div>

            <div className="space-y-4">
              {data.ticketTypes.map((ticket, index) => (
                <div key={ticket.id} className="rounded-lg border bg-background p-4">
                  <div className="mb-3 flex items-center justify-between">
                    <h4 className="font-semibold">Ticket Type {index + 1}</h4>
                    {data.ticketTypes.length > 1 && (
                      <Button variant="ghost" size="sm" onClick={() => removeTicketType(ticket.id)} {...({} as any)}>
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label {...({} as any)}>Ticket Type *</Label>
                      <Select
                        value={ticket.ticketType}
                        onValueChange={value => updateTicketType(ticket.id, "ticketType", value)}
                      >
                        <SelectTrigger {...({} as any)}>
                          <SelectValue placeholder="Select type" {...({} as any)} />
                        </SelectTrigger>
                        <SelectContent {...({} as any)}>
                          <SelectItem value="DEFAULT" {...({} as any)}>
                            Default
                          </SelectItem>
                          <SelectItem value="OFFLINE" {...({} as any)}>
                            Offline
                          </SelectItem>
                          <SelectItem value="ONLINE" disabled {...({} as any)}>
                            Online
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {ticket.ticketType === "OFFLINE" && (
                      <div className="space-y-2">
                        <Label {...({} as any)}>Ticket URL *</Label>
                        <Input
                          placeholder="https://example.com/tickets/general"
                          value={ticket.url || ""}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            updateTicketType(ticket.id, "url", e.target.value)
                          }
                          {...({} as any)}
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Availability Slots */}
          <div className="rounded-lg border bg-card p-6">
            <div className="mb-4 flex items-center gap-2">
              <Settings className="h-5 w-5" />
              <h3 className="font-semibold">Availability Schedule</h3>
            </div>
            <div className="space-y-4">
              <div className="mb-3">
                <Label className="flex items-center mb-2" {...({} as any)}>
                  <Clock className="w-4 h-4 mr-1" />
                  Weekly Schedule
                  <HelpCircle className="w-4 h-4 ml-1 text-muted-foreground" />
                </Label>
                <p className="text-xs text-muted-foreground mb-3">
                  Set when this play service is available for booking
                </p>
              </div>

              <div className="space-y-3">
                {[
                  { day: "MONDAY", label: "Monday", key: "monday" },
                  { day: "TUESDAY", label: "Tuesday", key: "tuesday" },
                  { day: "WEDNESDAY", label: "Wednesday", key: "wednesday" },
                  { day: "THURSDAY", label: "Thursday", key: "thursday" },
                  { day: "FRIDAY", label: "Friday", key: "friday" },
                  { day: "SATURDAY", label: "Saturday", key: "saturday" },
                  { day: "SUNDAY", label: "Sunday", key: "sunday" },
                ].map(dayOption => {
                  const existingSlot = (data.availabilitySlots || []).find(slot => slot.day === dayOption.day);
                  return (
                    <div
                      key={dayOption.key}
                      className="grid grid-cols-1 md:grid-cols-[auto_2fr_2fr] gap-2 md:gap-4 items-center"
                    >
                      <div className="flex items-center space-x-2">
                        <Switch
                          checked={!!existingSlot}
                          onCheckedChange={(checked: boolean) => {
                            const currentSlots = data.availabilitySlots || [];
                            if (checked) {
                              updateData({
                                availabilitySlots: [
                                  ...currentSlots,
                                  {
                                    day: dayOption.day,
                                    startTime: "09:00",
                                    endTime: "17:00",
                                  },
                                ],
                              });
                            } else {
                              updateData({
                                availabilitySlots: currentSlots.filter(slot => slot.day !== dayOption.day),
                              });
                            }
                          }}
                          {...({} as any)}
                        />
                        <Label htmlFor={`avail-${dayOption.key}`} className="w-20 font-normal" {...({} as any)}>
                          {dayOption.label}
                        </Label>
                      </div>

                      {/* Start Time - Always rendered but disabled when switch is off */}
                      <TimeInput
                        defaultValue="09:00"
                        disabled={!existingSlot}
                        value={
                          (typeof existingSlot?.startTime === "string" ? existingSlot.startTime : "").substring(0, 5) ||
                          "09:00"
                        }
                        onChange={(value: string) => {
                          const currentSlots = data.availabilitySlots || [];
                          if (existingSlot) {
                            updateData({
                              availabilitySlots: currentSlots.map(slot =>
                                slot.day === dayOption.day ? { ...slot, startTime: value } : slot
                              ),
                            });
                          } else {
                            // If no slot exists yet, create one
                            updateData({
                              availabilitySlots: [
                                ...currentSlots,
                                {
                                  day: dayOption.day,
                                  startTime: value,
                                  endTime: "17:00",
                                },
                              ],
                            });
                          }
                        }}
                      />

                      {/* End Time - Always rendered but disabled when switch is off */}
                      <TimeInput
                        defaultValue="17:00"
                        disabled={!existingSlot}
                        value={
                          (typeof existingSlot?.endTime === "string" ? existingSlot.endTime : "").substring(0, 5) ||
                          "17:00"
                        }
                        onChange={(value: string) => {
                          const currentSlots = data.availabilitySlots || [];
                          if (existingSlot) {
                            updateData({
                              availabilitySlots: currentSlots.map(slot =>
                                slot.day === dayOption.day ? { ...slot, endTime: value } : slot
                              ),
                            });
                          } else {
                            // If no slot exists yet, create one
                            updateData({
                              availabilitySlots: [
                                ...currentSlots,
                                {
                                  day: dayOption.day,
                                  startTime: "09:00",
                                  endTime: value,
                                },
                              ],
                            });
                          }
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8">
          <div className="mb-4">
            <ValidationMessage message={!isValid && getValidationMessage() ? getValidationMessage()! : ""} />
          </div>
          <div className="flex items-center justify-between">
            <Button variant="outline" onClick={onPrev} className="gap-2 bg-transparent" {...({} as any)}>
              <ArrowLeft className="h-4 w-4" />
              Previous
            </Button>
            <Button onClick={onNext} disabled={!isValid} size="lg" className="gap-2" {...({} as any)}>
              Continue to Add-ons
              <span>→</span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
